import { db } from "../../helpers/firebase";
import {
  collection,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";

const COLLECTION_NAME = "userSettings";

/**
 * Settings Model - Handles user settings and Leo authorizations
 *
 * Data Structure:
 * {
 *   id: string,                    // User ID (same as auth UID)
 *   userId: string,                // User ID reference
 *
 *   // Leo AI Authorizations
 *   leoAuthorizations: {
 *     sendMessages: boolean,       // Send messages to tenants
 *     sendReminders: boolean,      // Send rent reminders
 *     applyLateFees: boolean,      // Apply late fees automatically
 *     coordinateMaintenance: boolean, // Coordinate maintenance requests
 *     processDeposits: boolean,    // Process security deposits
 *     screenApplicants: boolean,   // Screen new tenant applicants
 *     generateReports: boolean,    // Generate monthly reports
 *     scheduleInspections: boolean, // Schedule property inspections
 *     autoLeaseRenewal: boolean,   // Auto-send lease renewal reminders
 *     autoPaymentReceipts: boolean // Auto-send payment receipts
 *   },
 *
 *   // Notification Preferences
 *   notifications: {
 *     rentReminders: boolean,      // Get notified when rent is due
 *     repairUpdates: boolean,      // Receive repair updates
 *     leaseExpirations: boolean,   // Master: lease expiration alerts
 *     leaseExpirationAlert90: boolean, // Email 90 days before lease end (Leo renewal link)
 *     leaseExpirationAlert60: boolean,
 *     leaseExpirationAlert30: boolean,
 *     maintenanceAlerts: boolean,  // Maintenance request alerts
 *     tenantMessages: boolean,     // Tenant message notifications
 *     paymentConfirmations: boolean // Payment confirmation alerts
 *   },
 *
 *   // Automation Settings
 *   automation: {
 *     autoRentReminders: boolean,  // Auto-send rent reminders
 *     autoMaintenanceFollowup: boolean, // Auto-follow up on repairs
 *     autoLeaseRenewal: boolean,   // Auto-lease renewal reminders
 *     autoPaymentReceipts: boolean // Auto-payment receipts
 *   },
 *
 *   // Privacy & Data Settings
 *   privacy: {
 *     allowMarketing: boolean,    // Marketing communications
 *     allowSurveys: boolean,       // Product surveys & feedback
 *     dataSharing: boolean,        // Share data for improvements
 *     analyticsTracking: boolean   // Analytics and usage tracking
 *   },
 *
 *   // Payment Processing Settings
 *   paymentSetup: {
 *     isComplete: boolean,        // Whether payment processing is set up
 *     stripeAccountId: string,    // Stripe Connect account ID
 *     onboardingComplete: boolean, // Stripe onboarding completion status
 *     bankAccountConnected: boolean, // Bank account connection status
 *     lastUpdated: timestamp      // Last payment setup update
 *   },
 *
 *   // Portfolio / Investor metrics (used for DSCR on dashboard when backend does not provide debtService)
 *   portfolio: {
 *     monthlyDebtService: number | null,  // Monthly debt service in dollars (e.g. mortgage P&I)
 *   },
 *
 *   // UI Preferences
 *   preferences: {
 *     theme: string,               // 'light', 'dark', 'auto'
 *     language: string,            // 'en', 'es', etc.
 *     timezone: string,            // User's timezone
 *     dateFormat: string,          // 'MM/DD/YYYY', 'DD/MM/YYYY'
 *     currency: string,            // 'USD', 'EUR', etc.
   *     emailFrequency: string,    // 'immediate', 'daily', 'weekly'
   *     dashboardImportSectionCollapsed: boolean, // Dashboard: hide bulk CSV import panel
   *     demoModePortfolio?: boolean, // false = real dashboard data even when ?debug=true; unset/true = ghost portfolio in debug
   *   },
 *
 *   // Timestamps
 *   createdAt: timestamp,
 *   updatedAt: timestamp
 * }
 */

class SettingsModel {
  /**
   * Get user settings by user ID
   */
  static async getByUserId(userId) {
    try {
      const docRef = doc(db, COLLECTION_NAME, userId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }

      return null;
    } catch (error) {
      console.error("Error getting user settings:", error);
      throw error;
    }
  }

  /**
   * Create default settings for a new user
   */
  static async createDefaultSettings(userId) {
    try {
      const defaultSettings = {
        userId,
        leoAuthorizations: {
          sendMessages: true,
          sendReminders: true,
          applyLateFees: true,
          coordinateMaintenance: true,
          processDeposits: false,
          screenApplicants: false,
          generateReports: true,
          scheduleInspections: false,
          autoLeaseRenewal: true,
          autoPaymentReceipts: true,
        },
        notifications: {
          rentReminders: true,
          repairUpdates: true,
          leaseExpirations: true,
          /** Landlord email at 90 / 60 / 30 days before lease end (cron + Leo deep link) */
          leaseExpirationAlert90: true,
          leaseExpirationAlert60: true,
          leaseExpirationAlert30: true,
          maintenanceAlerts: true,
          tenantMessages: true,
          paymentConfirmations: true,
        },
        automation: {
          autoRentReminders: true,
          autoMaintenanceFollowup: false,
          autoLeaseRenewal: true,
          autoPaymentReceipts: true,
        },
        privacy: {
          allowMarketing: false,
          allowSurveys: false,
          dataSharing: true,
          analyticsTracking: true,
        },
        paymentSetup: {
          isComplete: false,
          stripeAccountId: null,
          onboardingComplete: false,
          bankAccountConnected: false,
          lastUpdated: serverTimestamp(),
        },
        portfolio: {
          monthlyDebtService: null,
        },
        /** Hybrid Compliance: default ON; users may opt out unless a mandatory-state asset forces lock (see ComplianceSettings) */
        highComplianceMode: true,
        preferences: {
          theme: "light",
          language: "en",
          timezone: "America/New_York",
          dateFormat: "MM/DD/YYYY",
          currency: "USD",
          emailFrequency: "immediate",
          /** Omit `demoModePortfolio` by default: with `?debug=true`, dashboard uses ghost data until explicitly set false. */
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const docRef = doc(db, COLLECTION_NAME, userId);
      await setDoc(docRef, defaultSettings);

      return { id: userId, ...defaultSettings };
    } catch (error) {
      console.error("Error creating default settings:", error);
      throw error;
    }
  }

  /**
   * Update specific settings
   */
  static async updateSettings(userId, updates) {
    try {
      const docRef = doc(db, COLLECTION_NAME, userId);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });

      // Return updated settings
      return await this.getByUserId(userId);
    } catch (error) {
      console.error("Error updating settings:", error);
      throw error;
    }
  }

  /**
   * Update Leo authorizations specifically
   */
  static async updateLeoAuthorizations(userId, authorizations) {
    try {
      const docRef = doc(db, COLLECTION_NAME, userId);
      await updateDoc(docRef, {
        leoAuthorizations: authorizations,
        updatedAt: serverTimestamp(),
      });

      return await this.getByUserId(userId);
    } catch (error) {
      console.error("Error updating Leo authorizations:", error);
      throw error;
    }
  }

  /**
   * Update payment setup status
   */
  static async updatePaymentSetup(userId, paymentSetupData) {
    try {
      const docRef = doc(db, COLLECTION_NAME, userId);
      await updateDoc(docRef, {
        paymentSetup: {
          ...paymentSetupData,
          lastUpdated: serverTimestamp(),
        },
        updatedAt: serverTimestamp(),
      });

      return await this.getByUserId(userId);
    } catch (error) {
      console.error("Error updating payment setup:", error);
      throw error;
    }
  }

  /**
   * Update notification preferences
   */
  static async updateNotifications(userId, notifications) {
    try {
      const docRef = doc(db, COLLECTION_NAME, userId);
      await updateDoc(docRef, {
        notifications,
        updatedAt: serverTimestamp(),
      });

      return await this.getByUserId(userId);
    } catch (error) {
      console.error("Error updating notifications:", error);
      throw error;
    }
  }

  /**
   * Update automation settings
   */
  static async updateAutomation(userId, automation) {
    try {
      const docRef = doc(db, COLLECTION_NAME, userId);
      await updateDoc(docRef, {
        automation,
        updatedAt: serverTimestamp(),
      });

      return await this.getByUserId(userId);
    } catch (error) {
      console.error("Error updating automation settings:", error);
      throw error;
    }
  }

  /**
   * Update privacy settings
   */
  static async updatePrivacy(userId, privacy) {
    try {
      const docRef = doc(db, COLLECTION_NAME, userId);
      await updateDoc(docRef, {
        privacy,
        updatedAt: serverTimestamp(),
      });

      return await this.getByUserId(userId);
    } catch (error) {
      console.error("Error updating privacy settings:", error);
      throw error;
    }
  }

  /**
   * Get or create settings for current user
   */
  static async getOrCreateSettings() {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      console.log("SettingsModel.getOrCreateSettings - User:", user);

      if (!user) {
        throw new Error("User not authenticated");
      }

      console.log(
        "SettingsModel.getOrCreateSettings - Getting settings for user:",
        user.uid
      );
      let settings = await this.getByUserId(user.uid);

      if (!settings) {
        console.log(
          "SettingsModel.getOrCreateSettings - No settings found, creating default"
        );
        settings = await this.createDefaultSettings(user.uid);
      }

      console.log(
        "SettingsModel.getOrCreateSettings - Returning settings:",
        settings
      );
      return settings;
    } catch (error) {
      console.error("SettingsModel.getOrCreateSettings - Error:", error);
      throw error;
    }
  }

  /**
   * Check if a specific Leo authorization is enabled
   */
  static async isLeoAuthorized(userId, authorizationKey) {
    try {
      const settings = await this.getByUserId(userId);
      if (!settings || !settings.leoAuthorizations) {
        return false;
      }

      return settings.leoAuthorizations[authorizationKey] || false;
    } catch (error) {
      console.error("Error checking Leo authorization:", error);
      return false;
    }
  }

  /**
   * Get all Leo authorizations for a user
   */
  static async getLeoAuthorizations(userId) {
    try {
      const settings = await this.getByUserId(userId);
      return settings?.leoAuthorizations || {};
    } catch (error) {
      console.error("Error getting Leo authorizations:", error);
      return {};
    }
  }

  /**
   * Persist whether the dashboard "Import your portfolio" section is collapsed.
   * Uses a dotted field path so existing preference keys are not replaced wholesale.
   */
  static async setDashboardImportSectionCollapsed(userId, collapsed) {
    try {
      const docRef = doc(db, COLLECTION_NAME, userId);
      await updateDoc(docRef, {
        "preferences.dashboardImportSectionCollapsed": !!collapsed,
        updatedAt: serverTimestamp(),
      });
      return await this.getByUserId(userId);
    } catch (error) {
      console.error(
        "Error updating dashboard import section preference:",
        error
      );
      throw error;
    }
  }
}

export default SettingsModel;
